import React, { useState } from 'react';
import { Edit, useForm } from '@refinedev/antd';
import { Form, Input, InputNumber, Switch, Button, Card, Row, Col, Select, Upload, message, Image } from 'antd';
import { PlusOutlined, MinusCircleOutlined, UploadOutlined, DeleteOutlined } from '@ant-design/icons';
import { LocalizedContentTabs, normalizeTranslations } from '../../components/LocalizedContentTabs';
import { axiosInstance, API_BASE_URL } from '../../providers/dataProvider';
import { ALL_DISH_CATEGORIES, CatalogSource, DishType } from '@wellverse/shared-ts';

const LANG_FIELDS = [
  { key: 'name', label: 'Name', required: true },
  { key: 'description', label: 'Description', textarea: true, rows: 2 },
];

interface DishCatalogImage {
  id: string;
  imageUrl: string;
}

export const DishCatalogEdit: React.FC = () => {
  const { formProps, saveButtonProps, queryResult } = useForm({ resource: 'dish-catalog' });
  const record = queryResult?.data?.data;
  const images: DishCatalogImage[] = record?.images ?? [];
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleDeleteImage = async (imageId: string) => {
    if (!record?.id) return;
    setDeletingId(imageId);
    try {
      await axiosInstance.delete(`${API_BASE_URL}/dish-catalog/${record.id}/images/${imageId}`);
      message.success('Image deleted');
      queryResult?.refetch();
    } catch (err: any) {
      message.error(err.response?.data?.message || 'Failed to delete image');
    } finally {
      setDeletingId(null);
    }
  };

  const customUpload = async ({ file, onSuccess, onError }: any) => {
    if (!record?.id) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axiosInstance.post(`${API_BASE_URL}/dish-catalog/${record.id}/images`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onSuccess?.(res.data);
      message.success('Image uploaded');
      queryResult?.refetch();
    } catch (err: any) {
      onError?.(err);
      message.error(err.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Edit resource="dish-catalog" saveButtonProps={saveButtonProps}>
      <Form
        {...formProps}
        layout="vertical"
        onFinish={(values: any) =>
          formProps.onFinish?.({
            ...values,
            translations: normalizeTranslations(values.translations, LANG_FIELDS, queryResult?.data?.data?.translations),
          })
        }
      >
        <Card title="Basic Info" size="small" style={{ marginBottom: 24 }}>
          <LocalizedContentTabs fields={LANG_FIELDS} showSharedDivider={false} />
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="Type" name="type" initialValue={DishType.DISH}>
                <Select
                  options={Object.values(DishType).map((t) => ({ label: t, value: t }))}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Category" name="category" rules={[{ required: true }]}>
                <Select
                  placeholder="Select category"
                  allowClear
                  showSearch
                  options={ALL_DISH_CATEGORIES.map((c) => ({
                    label: c.replace(/_/g, ' '),
                    value: c,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Standard Portion (g)" name="standardPortionGrams" rules={[{ required: true }]}>
                <InputNumber style={{ width: '100%' }} min={1} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="Aliases" name="aliases">
            <Select mode="tags" placeholder="e.g. BBQ Pork Rice, 叉烧饭" />
          </Form.Item>
          <Form.Item label="Image URL" name="imageUrl">
            <Input placeholder="https://..." />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Source" name="source">
                <Select placeholder="Select source">
                  <Select.Option value={CatalogSource.MANUAL}>{CatalogSource.MANUAL}</Select.Option>
                  <Select.Option value={CatalogSource.USDA}>{CatalogSource.USDA}</Select.Option>
                  <Select.Option value={CatalogSource.CFS}>{CatalogSource.CFS}</Select.Option>
                  <Select.Option value={CatalogSource.AI_GENERATED}>{CatalogSource.AI_GENERATED}</Select.Option>
                  <Select.Option value={CatalogSource.WELLCOME}>{CatalogSource.WELLCOME}</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Active" name="isActive" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card title="Nutrition" size="small" style={{ marginBottom: 24 }}>
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item label="Calories" name="calories" rules={[{ required: true }]}>
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Protein (g)" name="protein" rules={[{ required: true }]}>
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Fat (g)" name="fat" rules={[{ required: true }]}>
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Carbs (g)" name="carbs" rules={[{ required: true }]}>
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item label="Fiber (g)" name="fiber">
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Sodium (mg)" name="sodium">
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card title="Ingredients" size="small" style={{ marginBottom: 24 }}>
          <Form.List name="ingredients">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Row key={key} gutter={12} align="middle" style={{ marginBottom: 8 }}>
                    <Col span={8}>
                      <Form.Item {...restField} name={[name, 'displayName']} rules={[{ required: true }]} noStyle>
                        <Input placeholder="Display name" />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item {...restField} name={[name, 'ingredientCatalogId']} noStyle>
                        <Input placeholder="Catalog ID (optional)" />
                      </Form.Item>
                    </Col>
                    <Col span={5}>
                      <Form.Item {...restField} name={[name, 'weightGrams']} rules={[{ required: true }]} noStyle>
                        <InputNumber style={{ width: '100%' }} min={0} placeholder="Weight (g)" />
                      </Form.Item>
                    </Col>
                    <Col span={4}>
                      <Form.Item {...restField} name={[name, 'percentage']} noStyle>
                        <InputNumber style={{ width: '100%' }} min={0} max={1} step={0.01} placeholder="%" />
                      </Form.Item>
                    </Col>
                    <Col span={1}>
                      <MinusCircleOutlined onClick={() => remove(name)} />
                    </Col>
                  </Row>
                ))}
                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                  Add Ingredient
                </Button>
              </>
            )}
          </Form.List>
        </Card>

        <Card title="Variations" size="small" style={{ marginBottom: 24 }}>
          <Form.List name="variations">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Row key={key} gutter={12} align="middle" style={{ marginBottom: 8 }}>
                    <Col span={6}>
                      <Form.Item {...restField} name={[name, 'name']} rules={[{ required: true }]} noStyle>
                        <Input placeholder="Variation name" />
                      </Form.Item>
                    </Col>
                    <Col span={4}>
                      <Form.Item {...restField} name={[name, 'deltaCalories']} noStyle>
                        <InputNumber style={{ width: '100%' }} placeholder="Δ Cal" />
                      </Form.Item>
                    </Col>
                    <Col span={3}>
                      <Form.Item {...restField} name={[name, 'deltaProtein']} noStyle>
                        <InputNumber style={{ width: '100%' }} placeholder="Δ P" />
                      </Form.Item>
                    </Col>
                    <Col span={3}>
                      <Form.Item {...restField} name={[name, 'deltaFat']} noStyle>
                        <InputNumber style={{ width: '100%' }} placeholder="Δ F" />
                      </Form.Item>
                    </Col>
                    <Col span={3}>
                      <Form.Item {...restField} name={[name, 'deltaCarbs']} noStyle>
                        <InputNumber style={{ width: '100%' }} placeholder="Δ C" />
                      </Form.Item>
                    </Col>
                    <Col span={3}>
                      <Form.Item {...restField} name={[name, 'deltaSodium']} noStyle>
                        <InputNumber style={{ width: '100%' }} placeholder="Δ Na" />
                      </Form.Item>
                    </Col>
                    <Col span={1}>
                      <MinusCircleOutlined onClick={() => remove(name)} />
                    </Col>
                  </Row>
                ))}
                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                  Add Variation
                </Button>
              </>
            )}
          </Form.List>
        </Card>

        <Card title="Reference Images" size="small" style={{ marginBottom: 24 }}>
          {images.length > 0 && (
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              {images.map((img) => (
                <Col key={img.id}>
                  <div style={{ position: 'relative', display: 'inline-block' }}>
                    <Image src={img.imageUrl} alt="Reference" width={120} style={{ borderRadius: 4 }} />
                    <Button
                      type="primary"
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      loading={deletingId === img.id}
                      onClick={() => handleDeleteImage(img.id)}
                      style={{ position: 'absolute', top: 4, right: 4 }}
                    />
                  </div>
                </Col>
              ))}
            </Row>
          )}
          <Upload customRequest={customUpload} showUploadList={false} accept="image/*">
            <Button type="dashed" icon={<UploadOutlined />} loading={uploading}>
              Upload Reference Image
            </Button>
          </Upload>
        </Card>
      </Form>
    </Edit>
  );
};
